import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '@/components/Layout';
import { TourCard } from '@/components/TourCard';
import { TourFilters } from '@/components/TourFilters';
import { useTours } from '@/hooks/useTours';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Loader2, 
  Shield, 
  Users, 
  Star, 
  Sparkles, 
  Compass, 
  MapPin, 
  Camera,
  Heart,
  Phone,
  Palmtree,
  Waves
} from 'lucide-react';
import strLogo from '@/assets/str-logo.png';
import { Link } from 'react-router-dom';

const Tours = () => {
  const [filters, setFilters] = useState<any>({});
  const { tours, loading } = useTours();
  const { getConfig } = useSiteConfig();

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const filteredTours = tours.filter((tour) => {
    if (filters.category && tour.category_id !== filters.category) return false;
    if (filters.minPrice && tour.adult_price && tour.adult_price < filters.minPrice) return false;
    if (filters.maxPrice && tour.adult_price && tour.adult_price > filters.maxPrice) return false;
    return true;
  });

  const featuredTours = filteredTours.filter((tour) => tour.is_featured);

  const highlights = [
    { icon: Shield, title: "Fully Insured", description: "Complete safety coverage", color: "from-primary to-blue-600" },
    { icon: Users, title: "Expert Guides", description: "Bilingual local experts", color: "from-secondary to-red-500" },
    { icon: Star, title: "5-Star Service", description: "Exceptional experiences", color: "from-tropical to-yellow-500" },
    { icon: Heart, title: "Handpicked Tours", description: "Curated adventures", color: "from-palm to-green-500" }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Tours & Excursions | Servicio Turístico Rachel - Dominican Republic Adventures</title>
        <meta
          name="description"
          content="Explore our collection of amazing tours and excursions in Dominican Republic. From beach adventures to cultural experiences, find your perfect adventure with STR."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-tropical via-palm to-primary"></div>
        
        {/* Animated Blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-secondary/30 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        
        {/* Floating Icons */}
        <div className="absolute top-1/4 left-1/4 animate-float">
          <Compass className="w-10 h-10 text-white/30" />
        </div>
        <div className="absolute bottom-1/3 right-1/4 animate-float animation-delay-2000">
          <Camera className="w-8 h-8 text-tropical/50" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-float animation-delay-4000">
          <Palmtree className="w-12 h-12 text-white/20" />
        </div>
        <div className="absolute bottom-1/4 left-1/3 animate-float animation-delay-3000">
          <Waves className="w-8 h-8 text-white/30" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            {/* Logo */}
            <div className="mb-8 flex justify-center animate-fade-in-up">
              <img 
                src={strLogo} 
                alt="Servicio Turístico Rachel" 
                className="h-20 w-auto drop-shadow-2xl"
              />
            </div>

            <Badge className="mb-6 bg-white/20 backdrop-blur-sm text-white border-white/30 text-lg py-2 px-6 animate-fade-in-up animation-delay-200">
              <Compass className="w-4 h-4 mr-2" />
              Tours & Excursions
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up animation-delay-400">
              <span className="block">Discover</span>
              <span className="bg-gradient-to-r from-white via-tropical to-white bg-clip-text text-transparent">
                Amazing Adventures
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-10 animate-fade-in-up animation-delay-600">
              Experience the best of Dominican Republic with our carefully curated tours. 
              Beach paradises, cultural treasures, and thrilling adventures await.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-800">
              <Button 
                size="lg" 
                className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                onClick={() => document.getElementById('tours')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Browse Tours
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-6 hover:scale-105 transition-all"
                asChild
              >
                <Link to="/contact">
                  <Phone className="mr-2 h-5 w-5" />
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>

      {/* Highlights Strip */}
      <section className="py-12 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {highlights.map((item, index) => (
              <Card 
                key={index} 
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="flex flex-col items-center text-center p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-muted/20" id="tours">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="border-0 shadow-lg overflow-hidden">
                  {/* Gradient Header */}
                  <div className="h-2 bg-gradient-to-r from-primary via-secondary to-tropical"></div>
                  <CardContent className="p-0">
                    <TourFilters onFiltersChange={handleFiltersChange} />
                  </CardContent>
                </Card>
              </div>
            </aside>

            {/* Tours Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex flex-col justify-center items-center min-h-[400px] gap-4">
                  <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                  <p className="text-muted-foreground">Loading amazing tours...</p>
                </div>
              ) : (
                <>
                  {/* Featured Tours */}
                  {featuredTours.length > 0 && (
                    <div className="mb-16">
                      <div className="mb-8 animate-fade-in-up">
                        <Badge className="mb-4 bg-gradient-to-r from-tropical to-palm text-white border-0 text-base py-2 px-5">
                          <Star className="w-4 h-4 mr-2" />
                          Featured Adventures
                        </Badge>
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                          <span className="text-foreground">Our</span>
                          <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Top Picks</span>
                        </h2>
                        <p className="text-muted-foreground text-lg">
                          Our most popular and highly-rated experiences
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {featuredTours.map((tour, index) => (
                          <div 
                            key={tour.id} 
                            className="animate-fade-in-up"
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            <TourCard tour={tour} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Tours */}
                  <div>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 animate-fade-in-up">
                      <div>
                        <Badge className="mb-4 bg-gradient-to-r from-primary to-blue-600 text-white border-0 text-base py-2 px-5">
                          <Compass className="w-4 h-4 mr-2" />
                          All Experiences
                        </Badge>
                        <h2 className="text-3xl md:text-4xl font-bold mb-2">
                          <span className="text-foreground">{featuredTours.length > 0 ? 'All Tours' : 'Available'}</span>
                          <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Tours</span>
                        </h2>
                        <p className="text-muted-foreground">
                          <span className="font-bold text-primary">{filteredTours.length}</span> {filteredTours.length === 1 ? 'tour' : 'tours'} available
                        </p>
                      </div>
                    </div>

                    {filteredTours.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredTours.map((tour, index) => (
                          <div 
                            key={tour.id} 
                            className="animate-fade-in-up"
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <TourCard tour={tour} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Card className="border-0 shadow-lg">
                        <CardContent className="text-center py-16">
                          <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center mb-6">
                            <Compass className="w-10 h-10 text-muted-foreground" />
                          </div>
                          <h3 className="text-xl font-bold mb-2">No Tours Found</h3>
                          <p className="text-muted-foreground mb-6">
                            No tours found matching your criteria. Try adjusting your filters.
                          </p>
                          <Button onClick={() => setFilters({})}>
                            Clear Filters
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-tropical"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-10 left-10 animate-float">
          <Sparkles className="w-12 h-12 text-white/30" />
        </div>
        <div className="absolute bottom-10 right-10 animate-float animation-delay-2000">
          <Compass className="w-10 h-10 text-tropical/50" />
        </div>
        <div className="absolute top-1/2 right-1/4 animate-float animation-delay-3000">
          <Star className="w-8 h-8 text-white/20" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Ready for Your
              <span className="block text-tropical">Adventure?</span>
            </h2>
            <p className="text-xl md:text-2xl mb-8 opacity-90 animate-fade-in-up animation-delay-200">
              Book your tour today and let us create an unforgettable experience. 
              All tours include professional guides, transportation, and our 5-star service.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-4 mb-10 animate-fade-in-up animation-delay-400">
              {[
                { icon: Shield, text: "Fully Insured" },
                { icon: Users, text: "Expert Guides" },
                { icon: MapPin, text: "All DR Destinations" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <item.icon className="w-4 h-4" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-600">
              <Button 
                size="lg" 
                className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                asChild
              >
                <Link to="/contact">Contact Us Today</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-10 py-6 hover:scale-105 transition-all"
                asChild
              >
                <Link to="/transfers">View Transfers</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Tours;
